import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { updateEntity } from '@adornis/baseql/operations/mongo.js';
import { fromNetwork } from '@adornis/baseql/symbols.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import type { UserPermission } from '@adornis/users/db/types.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import type { UpdateResult } from 'mongodb';
import { bookingActive } from '../../bookings/api/bookingActive.js';
import { Booking } from '../../bookings/entities/Booking.js';
import { ContractAPI, ContractApplyOptions } from '../../contracts/api/ContractAPI.js';
import { AppliedContract } from '../../contracts/entities/AppliedContract.js';
import { Contract } from '../../contracts/entities/Contract.js';
import { ContractMetaInformation } from '../../contracts/entities/ContractMetaInformation.js';
import { ContractSupplement } from '../../contracts/entities/ContractSupplement.js';
import { Abwesenheit } from '../../db/abwesenheit.js';
import type { DebitorEntity } from '../../debitor-numbers/entities/DebitorEntity.js';
import { LegacyDebitorNumber } from '../../debitor-numbers/entities/LegacyDebitorNumber.js';
import { getIncrementingDebitorennummer } from '../../debitor-numbers/server/getIncrementingDebitorennummer.js';
import { BeschaeftigungsGenehmigung } from '../../employment-permits/entities/beschaeftigungs-genehmigung.js';
import {
  calculateLAKHoursUntilLastSchoolYear,
  LAK_ABRECHNUNGSGRUPPEN,
  personKeepsLAK,
} from '../../lak/server/calculateLAKForPerson.js';
import { MicrosoftUserData } from '../../microsoft-graph/entities/MicrosoftUserData.js';
import {
  enqueueMicrosoftRemovedUserSync,
  enqueueMicrosoftUserSync,
} from '../../microsoft-graph/server/microsoft-sync-queue.js';
import { sendDeletionNotificationMail } from '../../microsoft-graph/server/users/send-deletion-notification-mail.js';
import { abacFieldMiddleware, viewerCanOnPerson } from '../../permissions/middlewares/abacFieldMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { resolveRecalculateRolesForPersonID } from '../../permissions/server/resolvers/resolveRecalculateRolesForPersonID.js';
import { SepaMandat } from '../../sepa/entities/SepaMandat.js';
import { cleanupMandatesForRemovedPerson } from '../../sepa/server/cleanupMandatesForRemovedPerson.js';
import { getSepaMandatesForUser } from '../../sepa/server/resolvers/resolveSubscribeSepaMandatesForUser.js';
import { clearPublishFilterCache } from '../../server/martinusPublishFilter.js';
import { mongo } from '../../server/mongo.js';
import { jobTicketWrapperFromPerson } from '../../ticket/jobTicketWrapperFromPerson.js';
import { MitarbeiterDaten } from '../employees/entities/mitarbeiter-daten.js';
import { UserEmployeeDetailsActions } from '../employees/UserEmployeeDetailsPolicy.js';
import { loadChildIDs } from '../family/server/loadChildIDs.js';
import { loadParentIDs } from '../family/server/loadParentIDs.js';
import { unlinkFromAllParents } from '../family/server/unlinkFromAllParents.js';
import { expandForPersonSearchIndex } from '../list/server/personTokenSearch.js';
import { ProfilDaten } from '../profile/entities/ProfilDaten.js';
import { SchuelerDaten } from '../students/schueler-daten.js';
import { materializedUserSelectionSet } from './materialized-user-selectionset.js';

@Materialize({ selectionSet: materializedUserSelectionSet })
@Entity()
export class Person extends AdornisUser<Permission | UserPermission> implements DebitorEntity {
  public static override _class = 'Person';
  public static override _collectionName = 'adornisuser';

  constructor(doc: PartialEntityData<Person>) {
    super(doc);
  }

  @Field(() => Date)
  lastLogin: Maybe<Date>;

  @Field(() => String)
  kreditorenNr: Maybe<string>;

  @Field(() => String)
  debitorennummer: Maybe<string>;

  /**
   * The id of the system a person import was exported from (the state school register's Landes-ID).
   * Unique per person like the debitorennummer.
   */
  @Field(() => String)
  externalUniqueID: Maybe<string>;

  @Field(() => [LegacyDebitorNumber], { default: v => v ?? [] })
  legacyDebitorNumbers: Maybe<LegacyDebitorNumber[]>;

  /**
   * Fallback while a complaint about the receipts is investigated: the person gets the old per-entry receipts
   * instead of the ones derived from the settled OPOS processes. Only Admin and Buchhaltung see the switch.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  useLegacyQuittungen: Maybe<boolean>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => String, {
    resolve: function (this: Person) {
      return async () => {
        if (this.legacyDebNumbersFlattened) return this.legacyDebNumbersFlattened;
        if (!this.legacyDebitorNumbers || this.legacyDebitorNumbers.length === 0) return '';
        return this.legacyDebitorNumbers.map(d => d.debitorNumber).join(',');
      };
    },
  })
  legacyDebNumbersFlattened: Maybe<string>;

  /**
   * All searchable values of this person, tokenized, lowercased and expanded into every suffix of
   * each token - materialized into the person view and indexed there, so the user list search is a
   * prefix match on a multikey index instead of a regex scan over seven fields, and that prefix
   * match still finds substrings ("4466" finds the debitor number "2004466"). Always recomputed
   * (never served from a stored value): a rename must produce fresh tokens.
   *
   * Changing what this resolves to does NOT recompute the existing view documents - the materialized
   * index detects staleness by hashing the selection set, which stays the same when only the
   * resolver body changes - so it needs a migration rebuilding the view. See
   * `migrate-person-search-token-suffixes`.
   */
  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => [String], {
    resolve: function (this: Person) {
      return async () => {
        if (this.searchTokens) return this.searchTokens;
        return expandForPersonSearchIndex(
          this.profilDaten?.stammdaten?.vorname,
          this.profilDaten?.stammdaten?.nachname,
          this.email,
          this.debitorennummer,
          this.username,
          this.mitarbeiterDaten?.personalNr,
          this.nameAbbreviation,
          ...(this.legacyDebitorNumbers?.map(d => d.debitorNumber) ?? []),
        );
      };
    },
  })
  searchTokens: Maybe<string[]>;

  @Field(() => String)
  nameAbbreviation: Maybe<string>;

  @Field(() => MicrosoftUserData, { default: v => v ?? new MicrosoftUserData({}) })
  microsoftUserData: Maybe<MicrosoftUserData>;

  @Field(() => ProfilDaten, { default: v => v ?? new ProfilDaten({}) })
  profilDaten: Maybe<ProfilDaten>;

  @Field(() => SchuelerDaten, { default: v => v ?? new SchuelerDaten({}) })
  schuelerDaten: Maybe<SchuelerDaten>;

  /**
   * Readable only for whom the employee tab is open - the person document itself is also readable for e.g. the
   * Sekretariat of a school where an employee of another school is a Sorgeberechtigter.
   */
  @Field(() => MitarbeiterDaten, {
    middlewares: [abacFieldMiddleware(UserEmployeeDetailsActions.VIEW_EMPLOYEE_TAB)],
  })
  mitarbeiterDaten: Maybe<MitarbeiterDaten>;

  /**
   * The LAK hours earned up to the end of the last completed school year. Null for
   * everybody without a LAK (`keepsLAK`).
   */
  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => Float, {
    // computed from the contracts in server context, so the contract publish filter does not cover it
    middlewares: [abacFieldMiddleware(UserEmployeeDetailsActions.VIEW_EMPLOYEE_TAB)],
    resolve: function (this: Person | EntityData<Person>) {
      return () => {
        // rows projected out of the view carry the materialized value, null included
        if (!(this instanceof Person) && 'lakHoursUntilLastSchoolYear' in this)
          return this.lakHoursUntilLastSchoolYear ?? null;
        if (!this._id) return null;
        // raw documents are loaded unprojected: absent employee data or another Abrechnungsgruppe means no LAK
        const mitarbeiterDaten = this.mitarbeiterDaten;
        if (
          mitarbeiterDaten !== undefined &&
          !LAK_ABRECHNUNGSGRUPPEN.includes(
            mitarbeiterDaten?.anBeschaeftigung?.abrechnungsgruppe as (typeof LAK_ABRECHNUNGSGRUPPEN)[number],
          )
        )
          return null;
        return runInServerContext(() => calculateLAKHoursUntilLastSchoolYear(this._id));
      };
    },
  })
  lakHoursUntilLastSchoolYear: Maybe<number>;

  @Field(() => Boolean, {
    resolve: function (this: Person) {
      return () => (this._id ? personKeepsLAK(this._id) : false);
    },
  })
  hasLAK: Maybe<boolean>;

  @Field(() => [String], { default: v => v ?? [] })
  sorgeberechtigteKinderIDs: Maybe<string[]>;

  @DataLoadedField(() => [Person], ({ sorgeberechtigteKinderIDs }) => sorgeberechtigteKinderIDs ?? [])
  sorgeberechtigteKinder?: Person[];

  @DataLoadedField(() => [Person], ({ _id }) => ({ sorgeberechtigteKinderIDs: _id }))
  sorgeberechtigteEltern: Maybe<Person[]>;

  @Field(() => [String], { default: v => v ?? [] })
  spendenKinderIDs: Maybe<string[]>;

  @DataLoadedField(() => [Person], ({ spendenKinderIDs }) => spendenKinderIDs ?? [])
  spendenKinder?: Person[];

  @DataLoadedField(() => [Person], ({ _id }) => ({ spendenKinderIDs: _id }))
  spender: Maybe<Person[]>;

  /**
   * Everybody who shares a Sorgeberechtigten with this person - half-siblings included. Resolved here so the person
   * view stores it. The view depends on the Sorgeberechtigten: linking a new sibling writes only the parent.
   */
  @Dependencies<typeof Person>({
    classes: () => [Person],
    ids: async function () {
      if (!this._id) return [];
      const parentIDs = await loadParentIDs([this._id]);
      return [...parentIDs, ...(await loadChildIDs(parentIDs))];
    },
  })
  @Field(() => [Person], {
    resolve: function (this: Person) {
      return async () => {
        if (this.geschwister) return this.geschwister;
        const siblingIDs = (await loadChildIDs(await loadParentIDs([this._id]))).filter(id => id !== this._id);
        // the siblings themselves go through the viewer's publish filter
        return (await loadMongoData(Person, siblingIDs)).map(sibling => constructValue(sibling));
      };
    },
  })
  geschwister: Maybe<Person[]>;

  @Field(() => Boolean, { default: v => v ?? false })
  activated?: boolean;

  @Field(() => Date)
  lastConfirmationEmailSent: Maybe<Date>;

  @DataLoadedField(() => [Contract], ({ _id }) => ({ userID: _id }))
  contracts: Maybe<Contract[]>;

  @DataLoadedField(() => [Booking], ({ _id }) => ({ userID: _id }))
  buchungen: Maybe<Array<Booking>>;

  @Field(() => [Booking], {
    resolve: function (this: Person) {
      return async (gqlFields: BaseQLSelectionSet<Booking>) => {
        if (this.activeBuchungen) return this.activeBuchungen;
        const bookings = await loadMongoData(Booking, { userID: this._id });
        return bookings.map(b => constructValue(b)).filter(b => bookingActive(b));
      };
    },
  })
  activeBuchungen: Maybe<Booking[]>;

  @DataLoadedField(
    () => [Booking],
    ({ sorgeberechtigteKinderIDs, spendenKinderIDs }) => ({
      $or: [{ userID: { $in: sorgeberechtigteKinderIDs ?? [] } }, { userID: { $in: spendenKinderIDs ?? [] } }],
    }),
  )
  bookingsOfChildren: Maybe<Array<Booking>>;

  @Field(() => [Booking], {
    resolve: function (this: Person) {
      return async (gqlFields: BaseQLSelectionSet<Booking>) => {
        if (this.activeBuchungenOfChildren) return this.activeBuchungenOfChildren;
        const bookings = await loadMongoData(Booking, {
          $or: [
            { userID: { $in: this.sorgeberechtigteKinderIDs ?? [] } },
            { userID: { $in: this.spendenKinderIDs ?? [] } },
          ],
        });
        return bookings.map(b => constructValue(b)).filter(b => bookingActive(b));
      };
    },
  })
  activeBuchungenOfChildren: Maybe<Booking[]>;

  // this field is used to calculate the applied contract
  @Field(() => Date)
  dateForAppliedContract: Maybe<Date>;

  @Field(() => AppliedContract, {
    resolve: function (this: Person) {
      return async (gqlFields: BaseQLSelectionSet<AppliedContract>) => {
        // the user might not have selected all necessary fields for the contract. so we extend them here
        gqlFields.createdAt = 1;
        gqlFields.userID = 1;
        gqlFields.appliedDate = 1;
        gqlFields.forcedUserAppliedDate = 1;
        // the applied contract answers this itself, and it has the supplement runtimes the check reads at hand
        // there - redoing the check here would mean requesting all supplements on top of every contract
        gqlFields.active = 1;

        gqlFields.agInfos ??= {};
        gqlFields.agInfos.einstellungsdatum = 1;
        gqlFields.agInfos.gekuendigtZum = 1;
        gqlFields.agInfos.unterzeichnungsdatum = 1;

        gqlFields.agEingruppierung1 ??= {};
        gqlFields.agEingruppierung1.agVertragsInfos ??= {};
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze ??= {};
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.kostenstelleID = 1;
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle ??= {};
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle.parentIDs = 1;
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.prozentsatz = 1;
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle.school ??= {};
        gqlFields.agEingruppierung1.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle.school.name = 1;
        gqlFields.agEingruppierung1.agVertragsInfos.taetigkeit = 1;
        gqlFields.agEingruppierung1.agArbeitszeit ??= {};
        gqlFields.agEingruppierung1.agArbeitszeit.ferienEinarbeiten = 1;
        gqlFields.agEingruppierung1.agArbeitszeit.maxDeputat = 1;
        gqlFields.agEingruppierung1.agArbeitszeit.stunden = 1;
        gqlFields.agEingruppierung1.agArbeitszeit.zaehler = 1;
        gqlFields.agEingruppierung1.agLohnGehaltszahlungen ??= {};
        gqlFields.agEingruppierung1.agLohnGehaltszahlungen.fiktivBeginnStufe1 = 1;
        gqlFields.agEingruppierung1.agLohnGehaltszahlungen.tarifgruppe = 1;
        gqlFields.agEingruppierung1.agLohnGehaltszahlungen.monatlicheZahlungen ??= {};
        gqlFields.agEingruppierung1.agLohnGehaltszahlungen.monatlicheZahlungen.lohnart = 1;

        gqlFields.agEingruppierung2 ??= {};
        gqlFields.agEingruppierung2.agVertragsInfos ??= {};
        gqlFields.agEingruppierung2.agVertragsInfos.taetigkeit = 1;
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze ??= {};
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze.prozentsatz = 1;
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze.kostenstelleID = 1;
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle ??= {};
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle.parentIDs = 1;
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle.school ??= {};
        gqlFields.agEingruppierung2.agVertragsInfos.kostenstelleProzentsaetze.kostenstelle.school.name = 1;
        gqlFields.agEingruppierung2.agArbeitszeit ??= {};
        gqlFields.agEingruppierung2.agArbeitszeit.ferienEinarbeiten = 1;
        gqlFields.agEingruppierung2.agArbeitszeit.maxDeputat = 1;
        gqlFields.agEingruppierung2.agArbeitszeit.stunden = 1;
        gqlFields.agEingruppierung2.agArbeitszeit.zaehler = 1;
        gqlFields.agEingruppierung2.agLohnGehaltszahlungen ??= {};
        gqlFields.agEingruppierung2.agLohnGehaltszahlungen.fiktivBeginnStufe1 = 1;
        gqlFields.agEingruppierung2.agLohnGehaltszahlungen.tarifgruppe = 1;
        gqlFields.agEingruppierung2.agLohnGehaltszahlungen.monatlicheZahlungen ??= {};
        gqlFields.agEingruppierung2.agLohnGehaltszahlungen.monatlicheZahlungen.lohnart = 1;

        const contracts = await loadMongoData(
          Contract,
          { userID: this._id },
          { sort: { 'agInfos.einstellungsdatum': 1 } },
        );

        const applyDate = this.dateForAppliedContract ?? new Date();

        const activeContracts: AppliedContract[] = [];
        for (const contract of contracts) {
          const applied = await ContractAPI.getApplied(
            contract._id,
            new ContractApplyOptions({ applyDate }),
          )(gqlFields);

          if (applied?.active) activeContracts.push(applied as AppliedContract);
        }

        if (activeContracts.length === 0) {
          const last = contracts.at(-1);
          if (!last) return null;
          const applied = await ContractAPI.getApplied(last._id, new ContractApplyOptions({ applyDate }))(gqlFields);
          if (applied?.agInfos?.einstellungsdatum < applyDate) {
            activeContracts.push(applied as AppliedContract);
          }
        }

        return activeContracts.at(-1);
      };
    },
  })
  activeContract: Maybe<AppliedContract>;

  @DataLoadedField(() => [Abwesenheit], ({ _id }) => ({ personID: _id }))
  abwesenheiten: Maybe<Abwesenheit[]>;

  @DataLoadedField(() => [BeschaeftigungsGenehmigung], ({ _id }) => ({ userID: _id }))
  employmentPermits: Maybe<BeschaeftigungsGenehmigung[]>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(() => [SepaMandat], {
    resolve: function (this: Person) {
      return async () => {
        if (this.sepaMandats) return this.sepaMandats;
        const values = await getSepaMandatesForUser(
          this._id,
          selectionSet(() => SepaMandat, 1),
        );
        return values.map(v => constructValue(v) as SepaMandat);
      };
    },
  })
  sepaMandats: Maybe<SepaMandat[]>;

  get displayName() {
    return this.profilDaten?.stammdaten?.vorname || this.profilDaten?.stammdaten?.nachname
      ? `${this.profilDaten.stammdaten.vorname} ${this.profilDaten.stammdaten.nachname}`
      : '---';
  }

  getJobTicketWrapper() {
    return jobTicketWrapperFromPerson(this);
  }

  /**
   * Roles only change through the role operations (`saveRolesForPerson`, the recalculation). Forms load them for
   * their permission checks, and a plain save would send them back: once the server recalculated them in between,
   * that copy is stale and the server reads it as a role change - denied for anyone without `ChangeRoles`, written
   * back over the recalculation for anyone with it. The server keeps its check, so a crafted role change still fails.
   */
  override async save() {
    if (A.isServer || !this.has('roles')) return super.save();
    await this.commit();
    const { roles: _roles, ...withoutRoles } = this.toObject();
    /**
     * Not `constructValue`: on the client that merges into the cached instance and hands the roles right back. And
     * marked as received from the network, so the copy stays partial - a plain `new Person` fills in the default of
     * every field it was not given and would write those over the stored person.
     */
    const matchedCount = await updateEntity(new Person({ ...withoutRoles, [fromNetwork]: true }))();
    await this._save();
    return matchedCount;
  }

  static override async beforeCreate(entity: Person): Promise<void> {
    entity = await this._ensureDebitorennummer(entity);
    entity.username = entity.debitorennummer!;
    return super.beforeCreate(entity);
  }

  static override async afterCreate(entity: Person, result): Promise<void> {
    // make sure all necessary sections are created

    // reset publishfilter as the new user might now be visible
    clearPublishFilterCache();

    // schedule the entra account. Deliberately not created here: a person import creates thousands of
    // persons, and every one of them used to block on a Graph call - so a slow or unreachable Microsoft
    // stalled or failed the import itself. The sync creates the account moments later.
    await enqueueMicrosoftUserSync(entity._id);

    return super.afterCreate(entity, result);
  }

  static override async transformBeforeUpdate(oldEntity: EntityData<Person>, update: Person): Promise<void> {
    /**
     * A viewer the employee data is withheld from (see `mitarbeiterDaten`) received `null` for it, and saving the
     * person would write that `null` over the stored data. Keep the stored value - this also keeps such a viewer
     * from writing it at all.
     */
    if (
      update.has('mitarbeiterDaten') &&
      !(await viewerCanOnPerson(UserEmployeeDetailsActions.VIEW_EMPLOYEE_TAB, oldEntity))
    ) {
      update.mitarbeiterDaten = oldEntity.mitarbeiterDaten ? new MitarbeiterDaten(oldEntity.mitarbeiterDaten) : null;
    }
    return super.transformBeforeUpdate(oldEntity, update);
  }

  static override async beforeUpdate(oldEntity: EntityData<Person>, update: Person): Promise<void> {
    update = await this._ensureDebitorennummer(update, oldEntity.debitorennummer);

    // a username somebody else holds is a typing mistake, not a server fault. Without this check the
    // unique index answers instead, with a raw E11000 logged as a fatal error. `has` first: a partial
    // update from the client throws on reading a field it did not send.
    if (
      update.has('username') &&
      update.username &&
      update.username !== oldEntity.username &&
      (await this.checkIfPersonDataIsDuplicated({ username: update.username }))
    ) {
      throw new ExpectedError('Dieser Benutzername wird bereits verwendet.', { translationKey: 'username_in_use' });
    }

    return super.beforeUpdate(oldEntity as EntityData<AdornisUser>, update as unknown as AdornisUser);
  }

  static override async afterRemove(_id: string, oldEntity: EntityData<Person>): Promise<void> {
    // TODO for rotating numbers, the debitorennummer can be set free again

    // remove all user data like contracts and absences
    const contractCol = await getRawCollection<Contract>(Contract._collectionName);
    const contractSupplementCol = await getRawCollection<ContractSupplement>(ContractSupplement._collectionName);
    const contractMetaInformationCol = await getRawCollection<ContractMetaInformation>(
      ContractMetaInformation._collectionName,
    );
    const contracts = await contractCol.find({ userID: _id }, { projection: { _id: 1 } }).toArray();
    for (const contract of contracts) {
      await contractSupplementCol.deleteMany({ vertragID: contract._id });
      await contractMetaInformationCol.deleteMany({ contractID: contract._id });
    }
    await contractCol.deleteMany({ userID: _id });

    // remove absences
    const absenceCol = await getRawCollection<Abwesenheit>(Abwesenheit._collectionName);
    await absenceCol.deleteMany({ personID: _id });

    // check all groups where the user is a participant and remove the user. Entra follows below, not through group
    // syncs: those only see members they can match to a person, and this one is gone
    const bookingCol = mongo().collection<EntityData<Booking>>(Booking._collectionName);
    const bookings = await bookingCol.find({ userID: _id }, { projection: { _id: 1 } }).toArray();
    for (const booking of bookings) await bookingCol.deleteOne({ _id: booking._id });

    await cleanupMandatesForRemovedPerson({
      personID: _id,
      deletedBookingIDs: bookings.map(booking => booking._id),
    });

    // roles of the sbs should change after a child is deleted
    for (const parentID of await unlinkFromAllParents(_id)) {
      await resolveRecalculateRolesForPersonID(parentID)();
    }

    // the account in Entra is disabled and leaves our groups, not deleted - see disableRemovedUserInEntra
    await enqueueMicrosoftRemovedUserSync(oldEntity.microsoftUserData?.microsoftID ?? '');
    await sendDeletionNotificationMail(oldEntity);

    return super.afterRemove(_id, oldEntity as EntityData<AdornisUser>);
  }

  static override async afterUpdate(
    oldEntity: Person,
    newEntity: Person,
    result: UpdateResult<Document>,
  ): Promise<void> {
    // schedule the entra sync instead of running it here. A person is saved from many places, and none
    // of them should wait on Microsoft or fail because of it - the sync recomputes what the person
    // needs anyway, so nothing is lost by doing it a moment later.
    await enqueueMicrosoftUserSync(newEntity._id);

    const col = await getRawCollection<Person>(this._collectionName);
    await col.updateOne({ _id: newEntity._id }, { $set: { dateForAppliedContract: null } });

    return super.afterUpdate(oldEntity, newEntity, result);
  }

  private static async checkIfPersonDataIsDuplicated(countData) {
    const personCol = await getRawCollection<Person>(this._collectionName);
    return !!(await personCol.countDocuments({ _class: this._class, ...countData }));
  }

  private static async _ensureDebitorennummer(person: Person, oldEntityDebitorennummer: Maybe<string> = null) {
    if (!person.debitorennummer) {
      person.debitorennummer = await getIncrementingDebitorennummer();
    }

    // Check for duplicate debitorennr when changing manually
    if (person.debitorennummer && oldEntityDebitorennummer && person.debitorennummer !== oldEntityDebitorennummer) {
      if (await this.checkIfPersonDataIsDuplicated({ debitorennummer: person.debitorennummer })) {
        throw new ExpectedError('Diese Debitorennummer wird bereits verwendet.');
      }
    }

    return person;
  }
}
